import React, { useRef, useState } from "react";
import { Upload, Loader2, X, ImageIcon as Img } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ImageUpload = ({ value, onChange, testId = "image-upload", aspectClass = "aspect-square w-32" }) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image must be under 8 MB");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post("/admin/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(`${BACKEND_URL}${res.data.url}`);
      toast.success("Uploaded");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Upload failed";
      toast.error(typeof detail === "string" ? detail : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2.5" data-testid={testId}>
      {value ? (
        <div className="relative inline-block">
          <img src={value} alt="preview" className={`${aspectClass} object-cover rounded-lg border border-slate-200`} />
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute -top-2 -right-2 bg-slate-900 text-white p-1.5 rounded-full hover:bg-red-500 shadow-lg"
            data-testid={`${testId}-clear`}
            aria-label="Clear"
          >
            <X size={12} />
          </button>
        </div>
      ) : (
        <div
          onClick={() => !uploading && inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            handleFile(e.dataTransfer.files?.[0]);
          }}
          className={`${aspectClass} flex flex-col items-center justify-center border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
            dragOver ? "border-orange-400 bg-orange-50" : "border-slate-300 bg-slate-50 hover:border-slate-400"
          }`}
        >
          {uploading ? (
            <Loader2 size={20} className="animate-spin text-slate-400" />
          ) : (
            <>
              <Img size={20} className="text-slate-400" />
              <span className="text-[10px] mt-1 text-slate-500 uppercase tracking-wider">Drop or click</span>
            </>
          )}
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          type="text"
          placeholder="Or paste image URL..."
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="admin-input text-xs flex-1"
          data-testid={`${testId}-url`}
        />
        <input ref={inputRef} type="file" accept="image/*" hidden onChange={(e) => handleFile(e.target.files?.[0])} />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="admin-btn-secondary text-xs"
          data-testid={`${testId}-btn`}
        >
          {uploading ? <><Loader2 size={12} className="animate-spin" /> Uploading</> : <><Upload size={12} /> Upload</>}
        </button>
      </div>
    </div>
  );
};

export default ImageUpload;
